import {ByzzerTextInput} from '@byzzer/ui-components';
import React, {ReactNode} from 'react';
import './DodSavedSelectionModal.scss';
import {DodFactSet, DodLayoutPreset} from '@/types/DodRun';
import {DodPreset, DodPresetSummary, PresetMap, RecommendedSelections, RecommendedSelectionsMap, RecommendedSelectionsSummary} from '@/types/ApiTypes';
import {ModalAction, openModal, confirm} from "@/components/form";
import {createDodPreset, createRecommendedSelections, updateDodPreset} from "@/api";

const baseClassName = 'dod-saved-selection-modal';

export interface CreateDodPresetParams {
    description: ReactNode,
    preset: Partial<DodPreset | DodPreset<DodFactSet[]> | DodPreset<DodLayoutPreset>>,
    currentPreset?: DodPresetSummary
    dodPresetsMap: DodPresetSummary[];
    dodUniversalSelectionsMap?: RecommendedSelections[];
    title?: string;
    duplicateNameErrorTip?: string;
    duplicateUniversalSelectionNameErrorTip?: string;
    userEmail?: string;
    isDodSuperUser?: boolean;
    reloadDodUniversalSavedSelections?: () => void;
    setPreset: (type: keyof PresetMap | keyof RecommendedSelectionsMap, preset: DodPresetSummary | RecommendedSelectionsSummary | undefined) => void;
}

export async function openCreateDodPresetModal({
                                                   description,
                                                   preset,
                                                   currentPreset,
                                                   dodPresetsMap,
                                                   dodUniversalSelectionsMap,
                                                   userEmail,
                                                   isDodSuperUser = false,
                                                   title = 'Save Your Selections',
                                                   reloadDodUniversalSavedSelections,
                                                   setPreset,
                                                   duplicateNameErrorTip = 'There is already a Saved Selection with this name. Please rename your selection.',
                                                   duplicateUniversalSelectionNameErrorTip = 'There is already a Universal Saved Selection with this name. Please rename your selection.'
                                               }: CreateDodPresetParams): Promise<DodPreset | undefined> {

    
    let action = 'create';
    if (currentPreset?.id === -1) return;

    const isDuplicatePresetName = (state: DodPreset) => {
        return dodPresetsMap
            ?.map((presetData) => presetData.displayName?.toLowerCase())
            .includes(state.displayName?.trim()?.toLowerCase());
    };
    const isDuplicateUniversalSelectionName = (state: RecommendedSelections) => {
        return dodUniversalSelectionsMap
            ?.map((presetData) => presetData.displayName?.toLowerCase())
            .includes(state.displayName?.trim()?.toLowerCase());
    };

    // Commenting this code for BYZ-16744, Keeping the code for future reference.
    // if (currentPreset) {
    //     action = await openModal({
    //         title: 'Update Your Saved Selections',
    //         showCloseOption: false,
    //         content: <>
    //             <p>You made changes to your Saved Selection, <span
    //                 className={`${baseClassName}__preset-name`}>{currentPreset?.displayName}</span>.</p>
    //             <p>Do you want to update this Saved Selection or create a new one?</p>
    //         </>,
    //         actions: [{
    //             key: 'cancel',
    //             label: 'Cancel',
    //             type: 'negative',
    //             action({resolve}) {
    //                 resolve('cancel')
    //             }
    //         }, {
    //             key: 'create',
    //             label: 'Create New',
    //             action({resolve}) {
    //                 resolve('create')
    //             }
    //         }, {
    //             key: 'update',
    //             label: 'Update',
    //             action({resolve}) {
    //                 resolve('update')
    //             }
    //         },]
    //     });
    // }

    switch (action) {
        case 'update':
            // return updateDodPreset(currentPreset!.id, preset.values);
        case 'create':
            return openModal<Partial<DodPreset | DodPreset<DodFactSet[]>>>({
                className: baseClassName,
                title: title,
                initialState: preset,
                showCloseOption: false,
                content: ({close, state, setState}) => {

                    function handleDisplayNameChange(e: ByzzerChangeEvent<string>) {
                        setState(state => ({
                            ...state,
                            displayName: e.value
                        }))
                    }

                    return <>
                        {description}
                        <ByzzerTextInput name={'displayName'} placeholder={'Enter a title.'} value={state.displayName}
                                         onChange={handleDisplayNameChange}/>
                    </>
                },
                actions: [{
                    key: 'cancel',
                    type: 'negative',
                    label: 'Cancel',
                    action({resolve}) {
                        resolve();
                    }
                }, {
                    key: 'save',
                    label: 'Save',
                    getDisableTip({state}) {
                        if(!state.displayName){
                            return 'Please enter a title for your Saved Selection.'
                        }
                        if(isDuplicatePresetName(state)){
                            return duplicateNameErrorTip;
                        }
                        return "";
                    },
                    disableIf({state}) {
                        return !state.displayName || isDuplicatePresetName(state)
                    },
                    async action({resolve, setBusy, state}) {

                        try {
                            setBusy(true);
                            const preset = await createDodPreset({
                                displayName: state.displayName,
                                type: state.type,
                                values: state.values,
                            });
                            resolve(preset);
                        } finally {
                            setBusy(false)
                        }
                    }
                },
                ...(isDodSuperUser ? [
                    {
                    key: 'universalSave',
                    label: 'Universal Save',
                    getDisableTip({state}) {
                        if(!state.displayName){
                            return 'Please enter a title for your Universal Saved Selection.'
                        }
                        if(isDuplicateUniversalSelectionName(state)){
                            return duplicateUniversalSelectionNameErrorTip;
                        }
                        return "";
                    },
                    disableIf({state}) {
                        return !state.displayName || isDuplicateUniversalSelectionName(state)
                    },
                    
                    async action({ resolve, setBusy, state }: { resolve: (value?: RecommendedSelections) => void; setBusy: (busy: boolean) => void; state: Partial<RecommendedSelections> }) {
                        resolve();
                        if (state.displayName) {
                            const result = await confirm({
                                title: 'Are you sure you want to use the universal save?',
                                content: (
                                    <>
                                        <p>This will save "{state.displayName}" across all accounts.</p>
                                    </>
                                ),
                                yesLabel: 'Universal Save',
                                noLabel: 'Cancel',
                            });
                            if (result) {
                                // Handle the case where the user confirmed the universal save
                                try {
                                    setBusy(true);
                                    const preset = await createRecommendedSelections({
                                        displayName: state.displayName,
                                        type: state.type!,
                                        values: state.values!,
                                        createdByEmailId: userEmail || '',
                                    });
                                    if (preset && reloadDodUniversalSavedSelections && state.type) {
                                        setPreset(state.type, {
                                            ...preset,
                                            // @ts-ignore - this needs to be here to remove values and produce a valid preset summary
                                            values: undefined
                                        })
                                        reloadDodUniversalSavedSelections();
                                    }
                                } finally {
                                    setBusy(false);
                                }
                            }
                        }
                    }
                }
                ] : [])
            ] as ModalAction<DodPreset>[]
            });
    }
}

